import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { IconButton, Stack, Tooltip } from '@mui/material';
import { DeleteOutline, DoneAll } from '@mui/icons-material';
import ConfirmDeleteModal from 'components/shared/modals/ConfirmDeleteModal';
import useNotifications from 'hooks/notifications/useNotifications';

const EASE = 'cubic-bezier(0.32, 0.72, 0, 1)';

const actionSx = {
  transition: `color 200ms ${EASE}, transform 200ms ${EASE}`,
  '&:active': { transform: 'scale(0.92)' }
};

/** Acciones de la cabecera del panel: marcar todas como leídas y vaciar la bandeja. */
function NotificationMenuButtons({ canClear, unreadCount }) {
  const { t } = useTranslation(['notifications']);
  const { deleteAllNotifications, markAllNotificationAsRead } = useNotifications();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const markAllLabel = t('menu.markAll', {
    ns: 'notifications',
    defaultValue: 'Marcar todas como leídas'
  });
  const clearLabel = t('menu.clear', { ns: 'notifications', defaultValue: 'Vaciar la bandeja' });

  const handleConfirmClear = () => {
    setConfirmOpen(false);
    deleteAllNotifications();
  };

  return (
    <Stack direction="row" spacing={0.25} sx={{ flex: 'none' }}>
      <Tooltip title={markAllLabel}>
        <span>
          <IconButton
            size="small"
            aria-label={markAllLabel}
            disabled={unreadCount === 0}
            onClick={markAllNotificationAsRead}
            sx={{ ...actionSx, color: 'gold.main' }}>
            <DoneAll sx={{ fontSize: 18 }} />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title={clearLabel}>
        <span>
          <IconButton
            size="small"
            aria-label={clearLabel}
            disabled={!canClear}
            onClick={() => setConfirmOpen(true)}
            sx={{ ...actionSx, color: 'text.secondary', '&:hover': { color: 'sessionStatus.absent.fg' } }}>
            <DeleteOutline sx={{ fontSize: 18 }} />
          </IconButton>
        </span>
      </Tooltip>
      <ConfirmDeleteModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmClear}
        title={t('menu.clearTitle', { ns: 'notifications', defaultValue: 'Vaciar notificaciones' })}
        message={t('menu.clearMessage', {
          ns: 'notifications',
          defaultValue: 'Vas a eliminar todas tus notificaciones.'
        })}
        confirmLabel={t('menu.clearConfirm', { ns: 'notifications', defaultValue: 'Vaciar' })}
      />
    </Stack>
  );
}

NotificationMenuButtons.propTypes = {
  canClear: PropTypes.bool.isRequired,
  unreadCount: PropTypes.number.isRequired
};

export default NotificationMenuButtons;
